/**
 * Clip-Transition-Envelope — die sichtbare Übergangs-Choreografie pro Clip.
 *
 * Jeder Szenen-Clip wird in der Engine in einen `position:absolute;inset:0`
 * Wrapper (`#${prefix}-wrap`) gepackt. Dieser Wrapper bekommt:
 *   - einen INTRO-Tween (kind-spezifisch: Fade, Wipe, Slide, Zoom, Blur, Flash)
 *     am Clip-Start,
 *   - einen OUTRO-Fade kurz vor Clip-Ende, damit der Clip sauber unter dem
 *     bereits einsetzenden nächsten Clip verschwindet (das Reel-Layout
 *     überlappt benachbarte Clips um `durationFrames`),
 *   - `visibility`-Toggles, damit inaktive Clips komplett aus der Render-
 *     Pipeline fallen (Performance bei teuren Effekten / Loops).
 *
 * Alle Tweens werden als String-JS für die projektweite GSAP-Timeline
 * emittiert (siehe `timeline.ts`) und funktionieren identisch im Composer-
 * Player, im Producer (Puppeteer) und in der Standalone-Preview.
 *
 * Wichtig: `none` bleibt ein harter Schnitt (kein Intro/Outro), damit
 * bestehende Reels unverändert schneiden.
 */
import { tl } from "../timeline.js";
import { FPS } from "../types.js";

/** Alle wählbaren Übergänge (Inspector + Engine teilen sich diese Liste). */
export const TRANSITION_KINDS = [
  "none",
  "crossfade",
  "wipeLeft",
  "wipeRight",
  "slideUp",
  "slideDown",
  "slideLeft",
  "slideRight",
  "zoomIn",
  "zoomOut",
  "blurDissolve",
  "glideUp",
  "flash",
] as const;

export type TransitionKind = (typeof TRANSITION_KINDS)[number];

type IntroSpec = {
  from: Record<string, unknown>;
  to: Record<string, unknown>;
  ease: string;
};

/**
 * Kind → { Start-Zustand, Intro-Ziel, Easing }. Der OUTRO ist für alle Kinds
 * ein sauberer Opacity-Fade (vermeidet Konflikte während des Clip-Overlaps).
 */
function introSpec(kind: TransitionKind): IntroSpec {
  switch (kind) {
    case "wipeRight":
      return { from: { opacity: 1, clipPath: "inset(0% 100% 0% 0%)" }, to: { opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }, ease: "power3.inOut" };
    case "wipeLeft":
      return { from: { opacity: 1, clipPath: "inset(0% 0% 0% 100%)" }, to: { opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }, ease: "power3.inOut" };
    case "slideUp":
      return { from: { opacity: 1, yPercent: 100 }, to: { opacity: 1, yPercent: 0 }, ease: "power3.out" };
    case "slideDown":
      return { from: { opacity: 1, yPercent: -100 }, to: { opacity: 1, yPercent: 0 }, ease: "power3.out" };
    case "slideLeft":
      return { from: { opacity: 1, xPercent: 100 }, to: { opacity: 1, xPercent: 0 }, ease: "power3.out" };
    case "slideRight":
      return { from: { opacity: 1, xPercent: -100 }, to: { opacity: 1, xPercent: 0 }, ease: "power3.out" };
    case "zoomIn":
      return { from: { opacity: 0, scale: 1.18 }, to: { opacity: 1, scale: 1 }, ease: "power2.out" };
    case "zoomOut":
      return { from: { opacity: 0, scale: 0.82 }, to: { opacity: 1, scale: 1 }, ease: "back.out(1.4)" };
    case "blurDissolve":
      return { from: { opacity: 0, filter: "blur(28px)" }, to: { opacity: 1, filter: "blur(0px)" }, ease: "power2.out" };
    case "glideUp":
      return { from: { opacity: 0, yPercent: 12 }, to: { opacity: 1, yPercent: 0 }, ease: "power2.out" };
    case "flash":
      return { from: { opacity: 0, filter: "brightness(3)" }, to: { opacity: 1, filter: "brightness(1)" }, ease: "power2.out" };
    case "crossfade":
    default:
      return { from: { opacity: 0 }, to: { opacity: 1 }, ease: "power1.inOut" };
  }
}

function isKnownKind(kind: string): kind is TransitionKind {
  return (TRANSITION_KINDS as readonly string[]).includes(kind);
}

/**
 * Baut die Init-Zeile + Tweens für die Übergangs-Hülle eines Clips.
 */
export function clipTransitionEnvelope(opts: {
  wrapSel: string;
  kind: string;
  durFrames: number;
  fromSec: number;
  endSec: number;
  clipDurSec: number;
  fps?: number;
}): { init: string; tweens: string[] } {
  const fps = opts.fps ?? FPS;
  const wrap = opts.wrapSel;
  const kind = isKnownKind(opts.kind) ? opts.kind : "none";
  const showAt = Math.max(0, opts.fromSec - 0.02);

  // Harter Schnitt: keine Intro-/Outro-Animation.
  if (kind === "none") {
    return {
      init: `gsap.set(${JSON.stringify(wrap)}, { opacity: 0, visibility: "hidden" });`,
      tweens: [
        tl.set(wrap, { opacity: 1, visibility: "visible" }, opts.fromSec),
        tl.set(wrap, { opacity: 0, visibility: "hidden" }, opts.endSec),
      ],
    };
  }

  // Intro-Dauer aus den Transition-Frames, aber so begrenzt, dass Intro +
  // Outro sicher in den Clip passen. Flash ist bewusst sehr kurz.
  const rawD = Math.max(2, opts.durFrames) / fps;
  const cap = Math.max(0.06, opts.clipDurSec * 0.45);
  const introD = kind === "flash" ? Math.min(0.14, cap) : Math.min(rawD, cap);
  const outroD = Math.min(introD, Math.max(0.06, opts.clipDurSec - introD - 0.04));
  const outroAt = Math.max(opts.fromSec + introD + 0.02, opts.endSec - outroD);

  const spec = introSpec(kind);

  const tweens: string[] = [
    tl.set(wrap, { visibility: "visible" }, showAt),
    tl.fromTo(wrap, spec.from, { ...spec.to, duration: introD, ease: spec.ease }, opts.fromSec),
    tl.to(wrap, { opacity: 0, duration: outroD, ease: "power1.in" }, outroAt),
    tl.set(wrap, { visibility: "hidden" }, opts.endSec),
  ];

  return {
    init: `gsap.set(${JSON.stringify(wrap)}, { opacity: 0, visibility: "hidden" });`,
    tweens,
  };
}
